import { isAnswered, reportProgress, type ReportFieldSpec } from '@fieldops/shared'
import type {
  PlacedField,
  ReportAnswer,
  ReportAnswers,
  ReportDefinition,
  ReportField,
  ReportStep,
} from './types.js'

/**
 * The reading of a form definition that the renderer works from.
 *
 * Kept out of the components because the same three questions — which step is a
 * field on, is it answered, how should it be drawn — are asked by the step
 * chips, the progress bar, the check sheet and the field cards. Answering them
 * in four places is how a form ends up saying "4 of 6" in the chip and showing
 * five cards.
 */

/** The steps, in order, with anything unsectioned gathered at the end. */
export function stepsOf(definition: ReportDefinition): ReportStep[] {
  const steps: ReportStep[] = definition.sections.map((section) => ({
    key: section.id,
    sectionKey: section.key,
    name: section.name,
    hint: section.hint,
    fields: definition.fields.filter((field) => field.sectionId === section.id),
  }))

  const loose = definition.fields.filter(
    (field) => field.sectionId === null || !steps.some((step) => step.key === field.sectionId),
  )

  if (loose.length > 0) {
    steps.push({
      key: 'unsectioned',
      // No section row exists, so there is no key to scope a server-side request
      // by. Null rather than the literal above, which would name nothing.
      sectionKey: null,
      name: definition.sections.length === 0 ? 'Questions' : 'Other questions',
      hint: null,
      fields: loose,
    })
  }

  return steps.filter((step) => step.fields.length > 0)
}

export function placedFields(definition: ReportDefinition): PlacedField[] {
  const names = new Map(definition.sections.map((section) => [section.id, section.name]))

  return definition.fields.map((field) => ({
    ...field,
    sectionName: field.sectionId === null ? null : names.get(field.sectionId) ?? null,
  }))
}

/**
 * The field specs the shared evaluator wants.
 *
 * The client computes findings as answers change so the sheet is instant and
 * correct offline; the server computes them again at submission. Both go
 * through `reviewReport`, from the same definition, so they cannot disagree.
 */
export function specsOf(definition: ReportDefinition): ReportFieldSpec[] {
  return placedFields(definition).map((field) => ({
    key: field.key,
    label: field.label,
    type: field.type,
    minLength: field.minLength,
    sectionName: field.sectionName,
  }))
}

export function progressOf(definition: ReportDefinition, answers: ReportAnswers) {
  return reportProgress(specsOf(definition), answers)
}

/** How far through one step is, for its chip. */
export function stepProgress(step: ReportStep, answers: ReportAnswers) {
  const asked = step.fields.filter((field) => field.type !== 'HEADING')
  const answered = asked.filter((field) => isAnswered(answers[field.key])).length
  return { answered, total: asked.length, complete: asked.length > 0 && answered === asked.length }
}

/** Which step a field is on, for the check sheet's jump. */
export function stepIndexOf(steps: ReportStep[], fieldKey: string): number {
  const index = steps.findIndex((step) => step.fields.some((field) => field.key === fieldKey))
  return index === -1 ? 0 : index
}

/**
 * A field's choices.
 *
 * `FormField.options` is JSON, which the importer filled from ServiceM8 and a
 * seed filled from the mockup, so it arrives as either a list of strings or a
 * list of `{ value, label }`. Both read as chips; neither is worth a migration
 * to normalise while the dual run is going.
 */
export function optionsOf(field: ReportField): string[] {
  if (!Array.isArray(field.options)) return []

  return field.options
    .map((option) => {
      if (typeof option === 'string') return option
      if (option !== null && typeof option === 'object' && 'value' in option) {
        return String((option as { value: unknown }).value)
      }
      return null
    })
    .filter((option): option is string => option !== null && option.length > 0)
}

export type FieldShape =
  | 'chips'
  | 'dropdown'
  | 'text'
  | 'textarea'
  | 'number'
  | 'boolean'
  | 'date'
  | 'photo'
  | 'signature'
  | 'heading'

/**
 * How a field is drawn.
 *
 * Chips are what make a generic renderer read as a purpose-built screen — a tap
 * instead of a dropdown and a scroll — so `AUTO` prefers them and only falls
 * back to a dropdown when the list is long enough that a wall of chips would be
 * worse than a picker. Six is the widest set in the seeded termite form, and it
 * still reads as two rows on a phone. Decision 54.
 */
export function shapeOf(field: ReportField): FieldShape {
  switch (field.type) {
    case 'HEADING':
      return 'heading'
    case 'PHOTO':
      return 'photo'
    case 'SIGNATURE':
      return 'signature'
    case 'BOOLEAN':
    case 'PASS_FAIL':
      return field.presentation === 'DROPDOWN' ? 'dropdown' : 'boolean'
    case 'DATE':
    case 'DATETIME':
      return 'date'
    case 'NUMBER':
    case 'DECIMAL':
    case 'RATING':
      return 'number'
    case 'TEXTAREA':
      return 'textarea'
    case 'TEXT':
      return 'text'
    case 'SELECT':
    case 'MULTI_SELECT': {
      if (field.presentation === 'DROPDOWN') return 'dropdown'
      if (field.presentation === 'CHIPS') return 'chips'
      const options = optionsOf(field)
      if (options.length === 0) return 'text'
      const longest = Math.max(...options.map((option) => option.length))
      return options.length <= 8 && longest <= 32 ? 'chips' : 'dropdown'
    }
  }
}

/** Whether a multi-select is being drawn, which decides what a chip tap does. */
export function isMulti(field: ReportField): boolean {
  return field.type === 'MULTI_SELECT'
}

/** Toggling one chip: a set for a multi-select, a radio that clears for a single. */
export function toggleChoice(
  field: ReportField,
  current: ReportAnswer,
  option: string,
): string[] | string | null {
  if (isMulti(field)) {
    const chosen = Array.isArray(current) ? current : []
    return chosen.includes(option)
      ? chosen.filter((item) => item !== option)
      : [...chosen, option]
  }

  return current === option ? null : option
}

/** An answer as one line, for the check sheet and the history rows. */
export function asText(value: ReportAnswer): string {
  if (value === null || value === undefined) return ''
  if (Array.isArray(value)) return value.join(', ')
  if (typeof value === 'boolean') return value ? 'Yes' : 'No'
  return String(value)
}
